import { Action, createReducer, on } from '@ngrx/store';

import { ExtractActions } from './extract.actions';
import { ExtractState } from './extract.state';
import { builtInTopics } from '../../features/extract/constans/default-topics';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { loadingPlaceholder } from '../../features/unified-datapoint/models/datapoint.model';
import { AppActions } from '../app/app.actions';

/**
 * Drops each column's records for transactions that left the scope, keeping identity for every
 * column that lost nothing, so a scope that only grew leaves `list` untouched by reference.
 */
function pruneToTransactions(list: ExtractState['list'], transactionIds: string[]): ExtractState['list'] {
  const keep = new Set(transactionIds);
  let hasChanged = false;

  const next = Object.entries(list ?? {}).reduce<ExtractState['list']>((acc, [columnId, records]) => {
    const ids = Object.keys(records ?? {});
    const keptIds = ids.filter((id) => keep.has(id));

    if (keptIds.length === ids.length) {
      acc[columnId] = records;
    } else {
      hasChanged = true;
      if (keptIds.length) {
        acc[columnId] = Object.fromEntries(keptIds.map((id) => [id, records[id]]));
      }
    }

    return acc;
  }, {});

  return hasChanged ? next : list;
}

export const initialState: ExtractState = {
  loading: false,
  loadingColumns: {},
  list: {},
  diffsByTopic: {},
  diffByDefault: true,
  updatingDatapoint: false,
  extractOrderIsDefault: false,
};

const featureReducer = measureReducer<ExtractState>(
  createReducer(
    initialState,
    // Pruned rather than wiped: a deal joining the scope must not refetch every other deal's cells.
    on(
      ExtractActions.StartLoadList,
      (state, action): ExtractState => ({
        ...state,
        error: undefined,
        list: pruneToTransactions(state.list, action.transactionIds),
      }),
    ),
    on(
      ExtractActions.LoadList,
      (state, action): ExtractState => ({
        ...state,
        error: undefined,
        loadingColumns: { ...state.loadingColumns, ...action.loadingColumns },
      })
    ),
    on(
      ExtractActions.LoadListSuccess,
      (state, action): ExtractState => ({
        ...state,
        // Merged per column, so a chunk carrying one new deal keeps the column's other deals.
        list: Object.entries(action.list).reduce<ExtractState['list']>(
          (acc, [columnId, records]) => {
            acc[columnId] = { ...acc[columnId], ...records };
            return acc;
          },
          { ...state.list },
        ),
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),
    on(
      ExtractActions.LoadListFail,
      (state, action): ExtractState => ({
        ...state,
        error: action.error,
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),

    on(
      ExtractActions.LoadExtractOrder,
      ExtractActions.UpdateExtractOrder,
      (state): ExtractState => ({ ...state, loading: true, error: undefined })
    ),
    on(
      ExtractActions.LoadExtractOrderSuccess,
      (state, action): ExtractState => ({
        ...state,
        loading: false,
        extractOrder: action.order?.length ? action.order : builtInTopics.map((t) => t.id),
        // No saved selection means the order is still the built-in default; keep the flag true so
        // asset built-in checklist topics apply. Loading an order (e.g. from an embedded deal view)
        // must not silently mark a non-customized session as customized.
        extractOrderIsDefault: !action.order?.length,
      })
    ),
    on(
      ExtractActions.UpdateExtractOrderSuccess,
      (state, action): ExtractState => ({
        ...state,
        loading: false,
        extractOrder: action.order,
        extractOrderIsDefault: false,
      })
    ),
    on(
      ExtractActions.LoadExtractOrderFail,
      (state, action): ExtractState => ({ ...state, loading: false, error: action.error })
    ),

    on(
      AppActions.LoadInitDataSuccess,
      (state, action): ExtractState => ({
        ...state,
        extractOrder: action.data.assetSettings?.extractTopicIds || builtInTopics.map((t) => t.id),
        extractOrderIsDefault: !action.data.assetSettings?.extractTopicIds,
      })
    ),

    on(ExtractActions.MakeDiffSuccess, ExtractActions.MakeDiffFail, (state, action): ExtractState => {
      const diff = state.diffsByTopic[action.topicId];
      if (diff?.requestId !== action.requestId) {
        return state;
      }

      const result = 'result' in action ? action.result : undefined;
      const diffLoadingByColumns = { ...diff.diffLoadingByColumns, [action.columnId]: false };
      const diffLoading = Object.values(diffLoadingByColumns).some(Boolean);

      return {
        ...state,
        diffsByTopic: {
          ...state.diffsByTopic,
          [action.topicId]: {
            ...diff,
            diffLoadingByColumns,
            diffLoading,
            diffCompleted: !diffLoading,
            diffError: diff.diffError || 'error' in action,
            diffColumns: { ...diff.diffColumns, [action.columnId]: !!result?.baseline && Object.keys(result.diffValues).length >= 2 },
            diffBaseline: { ...diff.diffBaseline, [action.columnId]: result?.baseline },
            diffList: { ...diff.diffList, [action.columnId]: result?.diffValues },
            diffMaximums: { ...diff.diffMaximums, [action.columnId]: result?.diffMaximums },
          },
        },
      };
    }),

    on(
      ExtractActions.MakeDiff,
      (state, action): ExtractState => ({
        ...state,
        diffsByTopic: {
          ...state.diffsByTopic,
          [action.topic.id]: {
            requestId: action.requestId,
            diffBaseline: {},
            diffColumns: {},
            diffList: {},
            diffLoading: !!action.topic.columns?.length && action.mode === 'diff',
            diffCompleted: !action.topic.columns?.length || action.mode === 'clear',
            diffLoadingByColumns: (action.topic.columns ?? []).reduce((acc, curr) => {
              acc[curr.id] = action.mode === 'diff';
              return acc;
            }, {}),
            diffMaximums: {},
          },
        },
      })
    ),

    on(
      ExtractActions.ClearDiffByTopic,
      (state, action): ExtractState => ({
        ...state,
        diffsByTopic: {
          ...state.diffsByTopic,
          [action.topicId]: {
            diffColumns: {},
            diffBaseline: {},
            diffList: {},
            diffLoading: false,
            diffCompleted: true,
            diffMaximums: {},
            diffLoadingByColumns: {},
          },
        },
      })
    ),

    on(ExtractActions.ClearDiff, (state, action): ExtractState => ({ ...state, diffsByTopic: initialState.diffsByTopic })),

    on(ExtractActions.DiffAll, (state, action): ExtractState => ({ ...state, diffByDefault: action.mode === 'diff' })),

    on(ExtractActions.UpdateDatapointSuccess, (state, action): ExtractState => {
      const colId = action.event.columnId;
      const transactionId = action.event.transactionId;
      const prevCol = state.list[colId] ?? {};
      const prev = prevCol[transactionId] ?? {};

      const merged = {
        ...prev,
        ...action.datapoint,
        ...(action.event.value !== undefined ? { value: action.event.value } : {}),
        ...(action.event.sourceReferences !== undefined ? { sourceReferences: action.event.sourceReferences } : {}),
      };

      return {
        ...state,
        updatingDatapoint: false,
        list: {
          ...state.list,
          [colId]: { ...prevCol, [transactionId]: merged },
        },
      };
    }),

    on(ExtractActions.UpdateDatapoint, (state, action): ExtractState => ({ ...state, updatingDatapoint: true })),
    on(
      ExtractActions.UpdateDatapointFail,

      (state, action): ExtractState => ({ ...state, updatingDatapoint: false })
    ),
    on(
      ExtractActions.UpdateDatapointSuccess,
      (state, action): ExtractState => ({
        ...state,
        selectedDatapointForEdit:
          state.selectedDatapointForEdit?.datapointField?.fieldHash === action.event.datapointHash
            ? {
                ...state.selectedDatapointForEdit,
                datapointValue: action.event.value ?? state.selectedDatapointForEdit.datapointValue,
                sourceReferences: action.event.sourceReferences ?? state.selectedDatapointForEdit.sourceReferences,
              }
            : state.selectedDatapointForEdit,
      })
    ),
    on(
      ExtractActions.UpdateSelectedDatapointForEdit,
      (state, action): ExtractState => ({
        ...state,
        selectedDatapointForEdit: action,
      })
    ),
    on(ExtractActions.ClearSelectedDatapointForEdit, (state): ExtractState => ({ ...state, selectedDatapointForEdit: undefined })),
    on(
      ExtractActions.ReRunSingleDatapointInitiated,
      (state, action): ExtractState => ({
        ...state,
        list: {
          ...state.list,
          [action.columnId]: {
            ...state.list[action.columnId],
            [action.transactionId]: {
              value: loadingPlaceholder,
            },
          },
        },
      })
    )
  )
);

export function reducer(state: ExtractState | undefined, action: Action): ExtractState {
  return featureReducer(state, action);
}
